import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Info } from 'lucide-react';
interface TooltipItem {
  name: string;
  badge: string;
  badgeColor: string;
  bgColor: string;
  borderColor: string;
  tooltip: string;
}
export function IngredientDemo() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const avoidItems: TooltipItem[] = [{
    name: 'Red 40, Yellow 5',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'Artificial colors linked to hyperactivity in children and potential carcinogenic effects. Banned in several European countries.'
  }, {
    name: 'Sodium Benzoate',
    badge: 'AVOID',
    badgeColor: 'bg-red-100 text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-100',
    tooltip: 'A preservative that can form benzene (a carcinogen) when combined with vitamin C. May trigger allergic reactions.'
  }];
  const limitItems: TooltipItem[] = [{
    name: 'Sugar, Corn Syrup',
    badge: 'LIMIT',
    badgeColor: 'bg-accent-100 text-accent-700',
    bgColor: 'bg-accent-50',
    borderColor: 'border-accent-100',
    tooltip: 'Excessive sugar intake is linked to obesity, type 2 diabetes, and heart disease. Corn syrup is a highly processed sweetener.'
  }];
  const safeItems: TooltipItem[] = [{
    name: 'Water, Citric Acid, Natural Flavors',
    badge: 'SAFE',
    badgeColor: 'bg-brand-100 text-brand-700',
    bgColor: 'bg-brand-50',
    borderColor: 'border-brand-100',
    tooltip: 'These are generally recognized as safe (GRAS). Citric acid is naturally found in citrus fruits. Natural flavors are derived from plant or animal sources.'
  }];
  const allItems = [...avoidItems, ...limitItems, ...safeItems];
  return <section className="py-20 bg-white overflow-hidden" data-id="element-2742" data-mp="3g0ui6uqu2cgh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2743" data-mp="3kycgzsb4zbar">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-2744" data-mp="3kwjm1a9vyfil">
          {/* Left: Text */}
          <div className="order-2 lg:order-1" data-id="element-2745" data-mp="3ilaeuxrvmo6f">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6" data-id="element-2746" data-mp="3ehui81ft4ntq">
              See It In Action
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-2747" data-mp="39k2gkfpd1ncc">
              IntelliEats breaks down complex ingredient lists into simple,
              actionable insights. Hover over each result to learn why it
              matters.
            </p>

            <div className="space-y-6" data-id="element-2748" data-mp="13por2zvwlq8">
              <div className="flex items-start gap-4" data-id="element-2749" data-mp="13zk763p7l3b">
                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0" data-id="element-2750" data-mp="3shvgd94oth5i">
                  <Check className="w-5 h-5 text-brand-600" data-id="element-2751" data-mp="3nju1z1i7i60d" />
                </div>
                <div data-id="element-2752" data-mp="oss9ijpteuoy">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-2753" data-mp="ou3n1b92bgt9">
                    Safe Ingredients
                  </h3>
                  <p className="text-slate-600" data-id="element-2754" data-mp="k0qwgvktozxv">
                    Green indicators for natural, healthy ingredients that are
                    safe to consume.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-2755" data-mp="wee5j3f0xrxe">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0" data-id="element-2756" data-mp="11aux67lfmof8">
                  <AlertCircle className="w-5 h-5 text-accent-600" data-id="element-2757" data-mp="16c2464fr3jku" />
                </div>
                <div data-id="element-2758" data-mp="1abt2qubir3zx">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-2759" data-mp="1aa9k4x2e4guz">
                    Limited Intake
                  </h3>
                  <p className="text-slate-600" data-id="element-2760" data-mp="1k2fci7wk10ap">
                    Amber warnings for ingredients like sugar or sodium that
                    should be moderated.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-2761" data-mp="1u1cxx1yoodph">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-2762" data-mp="1y1wt3zzp578z">
                  <X className="w-5 h-5 text-red-600" data-id="element-2763" data-mp="2vqjkdsey9um0" />
                </div>
                <div data-id="element-2764" data-mp="2vogl0hrwqp4m">
                  <h3 className="font-bold text-slate-900 text-lg" data-id="element-2765" data-mp="2qryi7ng510ux">
                    Ingredients to Avoid
                  </h3>
                  <p className="text-slate-600" data-id="element-2766" data-mp="34i20627zi8ek">
                    Red alerts for harmful additives, artificial colors, and
                    preservatives.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Visual Demo */}
          <div className="order-1 lg:order-2 relative" data-id="element-2767" data-mp="27tw1dvxqua87">
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-100 to-accent-100 rounded-3xl transform rotate-3 scale-105 -z-10 opacity-50" data-id="element-2768" data-mp="27v70vjk5ovti"></div>

            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden" data-id="element-2769" data-mp="2gsr42y1kx26r">
              {/* Mock Header */}
              <div className="bg-slate-50 border-b border-slate-100 p-4 flex items-center gap-2" data-id="element-2770" data-mp="147ol1p1k1wk">
                <div className="flex gap-1.5" data-id="element-2771" data-mp="3sb1yzq5myiqb">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-2772" data-mp="4j94ylk23yhl"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-2773" data-mp="8lnm9182uq6r"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-2774" data-mp="8jkr62s2i6v6"></div>
                </div>
                <div className="ml-4 text-xs font-medium text-slate-400" data-id="element-2775" data-mp="3qtv8sl0irufs">
                  Analysis Result
                </div>
                <div className="ml-auto flex items-center gap-1 text-xs text-slate-400" data-id="element-2776" data-mp="naa4bap39w1o">
                  <Info className="w-3 h-3" data-id="element-2777" data-mp="s9mzqju10bfv" />
                  Hover for details
                </div>
              </div>

              <div className="p-6 sm:p-8" data-id="element-2778" data-mp="dls3wukq1qyk">
                <div className="mb-6" data-id="element-2779" data-mp="diw7jaoivac8">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2780" data-mp="ijjw7ppa3f3a">
                    Scanned Text
                  </h4>
                  <div className="p-4 bg-slate-50 rounded-xl text-sm text-slate-600 font-mono leading-relaxed border border-slate-100" data-id="element-2781" data-mp="167l18on90ddy">
                    Water, Sugar, Corn Syrup, Artificial Colors (Red 40, Yellow
                    5), Sodium Benzoate, Citric Acid, Natural Flavors
                  </div>
                </div>

                <div data-id="element-2782" data-mp="1axx69qe4s8oj">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2783" data-mp="1axfdj3qogcld">
                    AI Detection
                  </h4>
                  <div className="space-y-2" data-id="element-2784" data-mp="16yy4genp4s1b">
                    {allItems.map((item, index) => <motion.div key={item.name} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: index * 0.15
                  }} className={`relative flex items-center justify-between p-3 ${item.bgColor} border ${item.borderColor} rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`} onMouseEnter={() => setHoveredItem(item.name)} onMouseLeave={() => setHoveredItem(null)} data-id="element-2785" data-mp="11ytn6meyrta1" data-mpi={index}>
                      
                        <span className="font-medium text-slate-900" data-id="element-2786" data-mp="2uw7clfjbaac1">
                          {item.name}
                        </span>
                        <span className={`px-2 py-1 ${item.badgeColor} text-xs font-bold rounded shrink-0 ml-2`} data-id="element-2787" data-mp="33vawglqaytrl">
                        
                          {item.badge}
                        </span>

                        {/* Tooltip */}
                        <AnimatePresence data-id="element-2788" data-mp="2b4aqw6cbphxt">
                          {hoveredItem === item.name && <motion.div initial={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                      }} exit={{
                        opacity: 0,
                        y: 8,
                        scale: 0.95
                      }} transition={{
                        duration: 0.2
                      }} className="absolute left-0 right-0 top-full mt-2 z-30 bg-slate-900 text-white text-xs rounded-xl p-3 shadow-xl leading-relaxed" data-id="element-2789" data-mp="2g4opyyvy5hjp">
                          
                              <div className="absolute -top-1.5 left-6 w-3 h-3 bg-slate-900 transform rotate-45" data-id="element-2790" data-mp="qrekiyechjya"></div>
                              {item.tooltip}
                            </motion.div>}
                        </AnimatePresence>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}